import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Feather from "@expo/vector-icons/Feather";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import * as Sentry from "@sentry/react-native";
import Purchases, {
  type CustomerInfo,
  type PurchasesEntitlementInfo,
  type PurchasesPackage,
} from "react-native-purchases";

import UnlockUsersBaseModal from "../Base/UnlockUsersBaseModal";

import PremiumFeatureBaseCard from "./Components/PremiumFeatureBaseCard";

import { Spacings } from "@/styling/spacings";
import { themes } from "@/Components/Styling/themeConfigs";
import { handlePurchase } from "@/services/integration/payment/revenuecat";

type Props = {
  isModalVisible: boolean;
  onRequestClose: () => void;
  onRequestRefresh: () => void;
};

function UnlockStageThreeModal({
  isModalVisible,
  onRequestClose,
  onRequestRefresh,
}: Props): React.JSX.Element {
  const currentTheme = themes.morning;

  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState<PurchasesPackage[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [activeEntitlement, setActiveEntitlement] =
    useState<PurchasesEntitlementInfo | null>(null);
  const isButtonDisabled = !selectedPlanId || isLoading;

  useEffect(() => {
    async function loadSubscriptionData(): Promise<void> {
      try {
        setIsLoading(true);
        // Check if the user has an active subscription.
        // This is tricky because active subscription means non-cancelled.
        // So we check for entitlement meaning if they have access to premium features.
        const customerInfo = await Purchases.getCustomerInfo();
        checkAndSetForActiveSubscription(customerInfo);

        // Fetch the current offerings
        const offeringsData = await Purchases.getOfferings();
        const possiblePlans = offeringsData.current?.availablePackages;
        setPlans(possiblePlans ?? []);

        if (possiblePlans && possiblePlans.length > 0 && possiblePlans[0]) {
          setSelectedPlanId(possiblePlans[0].identifier);
        }
      } catch (error) {
        Sentry.captureException(error);
        Alert.alert(
          "Error",
          "Something went wrong. Please try again or reach out to support@getkaiapp.com",
        );
      } finally {
        setIsLoading(false);
      }
    }
    loadSubscriptionData();
  }, []);

  const checkAndSetForActiveSubscription = (
    customerInfo: CustomerInfo,
  ): boolean => {
    const entitlements = customerInfo.entitlements.active;
    setActiveEntitlement(Object.values(entitlements)[0] || null);
    return Object.keys(entitlements).length > 0;
  };

  const styles = StyleSheet.create({
    premiumOptionsContainer: {
      width: "100%",
      justifyContent: "space-between",
    },
    premiumOption: {
      flex: 1,
      alignItems: "center",
      paddingVertical: Spacings.s1,
      marginTop: Spacings.s2,
      marginHorizontal: 4,
      backgroundColor: "#f5f5f5",
      borderRadius: 10,
      borderWidth: 0.5,
      borderColor: "#3b5998",
    },
    premiumOptionSelected: {
      borderWidth: 1.5,
      borderColor: "#3b5998",
    },
    sectionHeader: {
      fontSize: currentTheme.typography.h2.size.mobile,
      fontWeight: "bold",
      marginTop: Spacings.s2,
      marginBottom: Spacings.s1,
    },
    button: {
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      marginVertical: Spacings.s2,
      paddingHorizontal: Spacings.s3,
      height: 50,
      backgroundColor: "#3b5998",
      borderWidth: 1,
      borderColor: "black",
    },
    buttonDisabled: {
      backgroundColor: "#667cabff",
      borderColor: "#ccc",
    },
    buttonText: {
      color: "#eee",
      fontSize: currentTheme.typography.h2.size.mobile,
      fontWeight: "bold",
    },
    disabledButtonText: {
      color: "#ccc",
    },
    activePlanContainer: {
      backgroundColor: "#f5f5f5",
      borderRadius: 10,
      padding: Spacings.s3,
      marginBottom: Spacings.s3,
      alignItems: "center",
    },
    activePlanTitle: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: Spacings.s1,
    },
    activePlanText: {
      fontSize: 16,
      textAlign: "center",
      marginBottom: Spacings.s1,
    },
    textAlignCenter: {
      textAlign: "center",
    },
  });

  const handleSubscribe = async (planId: string): Promise<void> => {
    setIsLoading(true);
    try {
      const result = await handlePurchase(planId, plans, true);
      if (result.success) {
        onRequestRefresh();
        onRequestClose();
      }
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert(
        "Error",
        "Something went wrong while processing your subscription. Please try again or reach out to support@getkaiapp.com",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const modalBody: React.JSX.Element = (
    <View>
      <Text style={styles.sectionHeader}>🏆 Upgrade to Premium</Text>

      <View style={styles.premiumOptionsContainer}>
        {plans.length === 0 ? (
          <Text style={styles.textAlignCenter}>
            No subscription plans available at the moment.
          </Text>
        ) : (
          plans.map((plan) => (
            <Pressable
              key={plan.identifier}
              style={[
                styles.premiumOption,
                selectedPlanId === plan.identifier &&
                  styles.premiumOptionSelected,
              ]}
              onPress={() => setSelectedPlanId(plan.identifier)}
            >
              <Text
                style={{
                  fontWeight:
                    selectedPlanId === plan.identifier ? "bold" : "normal",
                }}
              >
                {plan.product.title}
              </Text>
              <Text>
                {plan.product.pricePerMonthString} {"per month"}
              </Text>
            </Pressable>
          ))
        )}
      </View>

      <Text style={styles.sectionHeader}>{"What's included:"}</Text>

      <PremiumFeatureBaseCard
        iconComponent={<Feather name="users" size={24} color="black" />}
        title={"Additional Users"}
        description={`See up to 200 profiles`} // TODO: dynamic number from backend when unlocking stage three is available on the backend
      />
      {/* <PremiumFeatureCard
        iconComponent={<Feather name="filter" size={24} color="black" />}
        title={"Advanced Filters (coming soon)"}
        description={"Filter by specific attributes and preferences"}
      />
      <PremiumFeatureCard
        iconComponent={<Feather name="filter" size={24} color="black" />}
        title={"Travel Mode (coming soon)"}
        description={"Explore profiles in other cities and countries "}
      />
      <PremiumFeatureCard
        iconComponent={<SimpleLineIcons name="badge" size={24} color="black" />}
        title={"Custom Badges (coming soon)"}
        description={"Stand out with a custom badge to show your support"}
      /> */}
      <PremiumFeatureBaseCard
        iconComponent={
          <MaterialCommunityIcons
            name="advertisements-off"
            size={24}
            color="black"
          />
        }
        title={"Ad-Free Guarantee"}
        description={"Enjoy peace of mind—no ads now or in the future"}
      />

      {activeEntitlement ? (
        <View style={styles.activePlanContainer}>
          <Feather name="check-circle" size={48} color="#3b5998" />
          <Text style={styles.activePlanTitle}>Premium Active</Text>
          <Text style={styles.activePlanText}>
            You have full access to all premium features.
          </Text>
        </View>
      ) : (
        <Pressable
          disabled={isButtonDisabled}
          style={[styles.button, isButtonDisabled && styles.buttonDisabled]}
          onPress={() => handleSubscribe(selectedPlanId)}
        >
          {isLoading ? (
            <ActivityIndicator size="large" color="#eee" />
          ) : (
            <Text
              style={[
                styles.buttonText,
                !selectedPlanId && styles.disabledButtonText,
              ]}
            >
              Subscribe Now
            </Text>
          )}
        </Pressable>
      )}
    </View>
  );

  return (
    <UnlockUsersBaseModal
      title={"Want to See More People?"}
      modalBody={modalBody}
      isModalVisible={isModalVisible}
      onRequestClose={onRequestClose}
    />
  );
}

export default UnlockStageThreeModal;
